/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {useIntl} from 'react-intl'

/**
 * Provides localized password validation messages for form input fields.
 *
 * @returns {Object} An object containing various password validation messages:
 *   - `required`: Message indicating that a password is required.
 *   - `minChars`: Message indicating the minimum number of characters required.
 *   - `uppercase`: Message indicating the requirement for at least one uppercase letter.
 *   - `lowercase`: Message indicating the requirement for at least one lowercase letter.
 *   - `number`: Message indicating the requirement for at least one numerical digit.
 *   - `specialChar`: Message indicating the requirement for at least one special character.
 */
export const usePasswordValidationMessages = () => {
    const {formatMessage} = useIntl()

    return {
        required: {
            create_password: formatMessage({
                defaultMessage: 'Please create a password.',
                id: 'password_field.error.required_password'
            }),
            current_password: formatMessage({
                defaultMessage: 'Please enter your current password.',
                id: 'password_field.error.required_current_password'
            }),
            new_password: formatMessage({
                defaultMessage: 'Please provide a new password.',
                id: 'password_field.error.required_new_password'
            })
        },
        minChars: formatMessage({
            defaultMessage: 'Password must contain at least 8 characters.',
            id: 'password_field.error.minimum_characters'
        }),
        uppercase: formatMessage({
            defaultMessage: 'Password must contain at least one uppercase letter.',
            id: 'password_field.error.uppercase_letter'
        }),
        lowercase: formatMessage({
            defaultMessage: 'Password must contain at least one lowercase letter.',
            id: 'password_field.error.lowercase_letter'
        }),
        number: formatMessage({
            defaultMessage: 'Password must contain at least one number.',
            id: 'password_field.error.contain_number'
        }),
        specialChar: formatMessage({
            defaultMessage: 'Password must contain at least one special character.',
            id: 'password_field.error.special_character'
        })
    }
}

/**
 * Provides mapping of password requirements that have/haven't been met
 * @param {string} value - The password to validate
 * @returns {Object} - True/false for each password validation rule
 */
export const validatePassword = (value) => {
    return {
        hasMinChars: value && value.length >= 8 ? true : false,
        hasUppercase: value && /[A-Z]/.test(value) ? true : false,
        hasLowercase: value && /[a-z]/.test(value) ? true : false,
        hasNumber: value && /\d/.test(value) ? true : false,
        hasSpecialChar: value && /[#!&$%*+/?=^`~}|{]/.test(value) ? true : false
    }
}
